import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Lock, ArrowRight } from "lucide-react";
import { adminApi, setToken, apiError } from "@/lib/adminApi";
import { LiquidBackground } from "@/components/primitives/LiquidBackground";

export default function AdminLogin({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await adminApi.login(password);
      setToken(data.token);
      toast.success("Welcome back");
      onAuthed();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-ink px-6 text-foreground">
      <LiquidBackground />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass-strong relative z-10 w-full max-w-md rounded-3xl p-8 md:p-10"
      >
        <div className="mb-8 flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-amber/15 ring-1 ring-amber/40">
            <ShieldCheck className="h-5 w-5 text-amber" />
          </span>
          <div>
            <p className="font-display text-2xl leading-none">THS Admin</p>
            <p className="mt-1 text-xs text-muted-foreground">Bookings dashboard</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-muted-foreground">Password</span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                data-testid="admin-password-input"
                autoFocus
                className="w-full rounded-2xl border border-white/12 bg-white/5 py-4 pl-11 pr-5 text-sm outline-none transition-colors focus:border-amber/60 focus:bg-white/8"
              />
            </div>
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 text-sm font-semibold text-ink transition-colors hover:bg-gold disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
        <a href="/" className="mt-6 block text-center text-xs text-muted-foreground transition-colors hover:text-foreground">
          ← Back to website
        </a>
      </motion.div>
    </div>
  );
}
